import type { ChipSurface } from '@/components/parts/chip'
import type { News } from '@/types/microcms/news'

/** ニュース一覧 1 ページあたりの件数（Figma のカード 3 列 × 4 行） */
export const NEWS_PAGE_SIZE = 12

/** 一覧のページ番号から URL を作る。1 ページ目は /news/ */
export const getNewsListHref = (page: number): string =>
  page <= 1 ? '/news/' : `/news/page/${page}/`

/** FRAIM のサイトに本文がある記事。詳細は fraim.co.jp で開く */
export const isFraimNews = (news: News): boolean =>
  Array.isArray(news.detailTargets) && news.detailTargets.includes('FRAIM')

export const getNewsHref = (news: News): string =>
  isFraimNews(news)
    ? `https://fraim.co.jp/news/${news.id}`
    : `/news/${news.id}/`

/** 一覧に出す日付。microCMS の作成日時を上書きしている記事はそちらを使う */
export const getNewsDate = (news: News): string =>
  news.overwriteCreatedAt || news.createdAt

const isLawgueCategory = (
  value: unknown
): value is { name: string; sites: string[] } =>
  typeof value === 'object' &&
  value !== null &&
  'name' in value &&
  'sites' in value &&
  Array.isArray(value.sites) &&
  value.sites.includes('LAWGUE')

/**
 * 記事のカテゴリー名（LAWGUE サイトのもののみ）。
 * newsCategory は単一参照・複数参照・文字列のいずれでも返ってくるので全部受ける
 */
export const getNewsCategoryNames = (news: News): string[] => {
  const raw: unknown = news.newsCategory ?? news.category
  const list: unknown[] = Array.isArray(raw) ? raw : raw ? [raw] : []

  return list.flatMap((category) => {
    if (typeof category === 'string') return [category]
    return isLawgueCategory(category) ? [category.name] : []
  })
}

/**
 * カードに並べるタグ。FRAIM の記事は旧実装のバッジに当たる「FRAIM」を先頭に置く。
 * 旧実装と同じく黒地に白の太字にして、カテゴリーと区別する
 */
export const getNewsTags = (
  news: News
): { group: string; label: string; surface?: ChipSurface }[] => [
  ...(isFraimNews(news)
    ? [{ group: 'site', label: 'FRAIM', surface: 'fraim' as const }]
    : []),
  ...getNewsCategoryNames(news).map((label) => ({ group: 'category', label }))
]
